import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { Collapse, Divider, Icon, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { ConfigInput, ConfigSortableList, IconPicker } from '@sierralabs/core-ui';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteNavigationFilter } from '../../hooks/site-navigation/useSiteNavigationFilter';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledList = styled(List) `
  overflow: auto;
  &.component-edit .nav-item {
    pointer-events: none;
  }
`;
const StyledListItem = styled(ListItem) `
  padding: 10px 16px;
`;
export const SiteNavigation = props => {
    var _a;
    const { isHover, editing, hoverProps } = useSiteContext();
    const component = useSubscribeComponentChanges(SiteNavigationPlugin, props);
    const { onNavigationItemClicked } = props;
    const { filter } = useSiteNavigationFilter();
    // only filter in view mode
    const navigationProp = ((_a = component.props) === null || _a === void 0 ? void 0 : _a.navigation) || [];
    const navigation = editing ? navigationProp : filter(navigationProp);
    const handleItemClick = (item) => {
        if (onNavigationItemClicked) {
            onNavigationItemClicked(item);
        }
    };
    const classes = ['site-navigation-list'];
    if (editing) {
        classes.push('component-edit');
    }
    if (isHover(component)) {
        classes.push('component-hover');
    }
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteNavigationPlugin }, props),
        React.createElement(StyledList, Object.assign({ dense: true, className: classes.join(' ') }, hoverProps(component)), navigation.map((item, index) => (React.createElement(MenuItem, { item: item, key: `${item.id || item.path}-${index}`, onNavigationItemClicked: handleItemClick }))))));
};
const MenuItem = props => {
    const { item, onNavigationItemClicked } = props;
    const { label, icon, path, items = [] } = item;
    const isExpandable = items.length > 0;
    const [open, setOpen] = useState(false);
    const handleClick = () => {
        isExpandable && setOpen(!open);
        onNavigationItemClicked === null || onNavigationItemClicked === void 0 ? void 0 : onNavigationItemClicked(item);
    };
    const itemContent = (React.createElement(React.Fragment, null,
        icon && (React.createElement(ListItemIcon, null, typeof icon === 'string' ? React.createElement(Icon, null, icon) : icon)),
        React.createElement(ListItemText, { inset: !icon, primary: label }),
        isExpandable && React.createElement(Icon, null, open ? 'expand_less' : 'expand_more')));
    return (React.createElement(React.Fragment, null,
        path ? (React.createElement(StyledListItem, { button: true, to: path, className: 'nav-item', activeClassName: 'active', component: NavLink, onClick: handleClick }, itemContent)) : (React.createElement(StyledListItem, { button: true, className: 'nav-item', onClick: handleClick }, itemContent)),
        React.createElement(Collapse, { unmountOnExit: true, timeout: 'auto', in: open && isExpandable },
            React.createElement(Divider, null),
            React.createElement(List, { component: 'div', disablePadding: true }, items.map((item, index) => (React.createElement(MenuItem, { key: index, item: item, onNavigationItemClicked: onNavigationItemClicked })))))));
};
const StyledNavigationItemConfigDiv = styled.div `
  display: flex;
`;
export const SiteNavigationConfig = props => {
    var _a;
    const { component, update, insert, remove, move } = useConfigComponent(props.component);
    return (React.createElement(React.Fragment, null,
        React.createElement(ConfigSortableList, { data: ((_a = component.props) === null || _a === void 0 ? void 0 : _a.navigation) || [], keyIndicator: item => item.id || item.label, renderItem: (navigation, index) => (React.createElement(SiteNavigationItemConfig, { value: navigation, onChangeIcon: icon => {
                    update(`props.navigation[${index}].icon`, icon);
                }, onChangeLabel: label => {
                    update(`props.navigation[${index}].label`, label);
                }, onChangePath: path => {
                    update(`props.navigation[${index}].path`, path);
                } })), footer: React.createElement(SiteNavigationItemConfig, { onEnterKey: item => {
                    var _a;
                    if (!((_a = component.props) === null || _a === void 0 ? void 0 : _a.navigation)) {
                        update('props.navigation', []);
                    }
                    insert('props.navigation', item);
                } }), onMove: (sourceIndex, destinationIndex) => {
                move('props.navigation', sourceIndex, destinationIndex);
            }, onDelete: index => remove(`props.navigation[${index}]`) })));
};
const SiteNavigationItemConfig = ({ value, onChangeIcon, onChangeLabel, onChangePath, onEnterKey }) => {
    const [navigation, setNavigation] = useState(value || {});
    useEffect(() => {
        if (value) {
            setNavigation(value);
        }
    }, [value]);
    const handleAddNavigationItem = (event) => {
        if (!onEnterKey)
            return;
        switch (event.key) {
            case 'Enter':
                const { icon, label = '', path = '' } = navigation;
                onEnterKey === null || onEnterKey === void 0 ? void 0 : onEnterKey({ id: uuid(), icon, label, path });
                setNavigation({});
        }
    };
    return (React.createElement(StyledNavigationItemConfigDiv, null,
        React.createElement(IconPicker, { value: navigation.icon, onChange: icon => {
                setNavigation(Object.assign(Object.assign({}, navigation), { icon }));
                onChangeIcon === null || onChangeIcon === void 0 ? void 0 : onChangeIcon(icon);
            } }),
        React.createElement(ConfigInput, { placeholder: 'Add Navigation', value: navigation.label || '', onChange: event => {
                setNavigation(Object.assign(Object.assign({}, navigation), { label: event.target.value }));
                onChangeLabel === null || onChangeLabel === void 0 ? void 0 : onChangeLabel(event.target.value);
            }, onKeyDown: event => handleAddNavigationItem(event) }),
        React.createElement(ConfigInput, { placeholder: '/path', value: navigation.path || '', onChange: event => {
                setNavigation(Object.assign(Object.assign({}, navigation), { path: event.target.value }));
                onChangePath === null || onChangePath === void 0 ? void 0 : onChangePath(event.target.value);
            }, onKeyDown: event => handleAddNavigationItem(event) })));
};
export const SiteNavigationPlugin = {
    name: 'Site Navigation',
    type: 'site-navigation',
    component: SiteNavigation,
    configurator: SiteNavigationConfig
};
//# sourceMappingURL=SiteNavigationPlugin.js.map